import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';

/**
 * Voimakortit — the deck of polarity power cards stored in book_chunks.
 *
 * Cards are fetched from /api/voimakortit (license-gated) and rendered as
 * a clickable list. Selecting a card opens a detail view that shows the
 * full markdown text using the same monospace typography as Entry.
 */
export default function Voimakortit() {
  const { t } = useT();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get('/voimakortit');
        if (!cancel) setCards(r.data?.cards || []);
      } catch (e) {
        if (!cancel) setErr(String(e?.response?.data?.detail || e.message || 'error'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Esc closes the detail view.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Strip the leading H1 from the markdown body when shown alongside its title.
  const bodyOf = (text) => {
    const lines = (text || '').split('\n');
    if (lines[0]?.startsWith('# ')) lines.shift();
    while (lines.length && lines[0].trim() === '') lines.shift();
    return lines.join('\n');
  };

  const cleanTitle = (title) =>
    (title || '').replace(/\s*[—–-]\s*Voimakortti.*$/i, '').trim();

  // Each card has a matching hero image in /public/voimakortit/{core_key}.jpg
  // (downloaded at build time so it's offline-safe inside the PWA).
  const imgFor = (card) =>
    card?.core_key
      ? `/voimakortit/${String(card.core_key).replace(/^core_/, '')}.jpg`
      : null;

  if (open) {
    const card = open;
    const img = imgFor(card);
    return (
      <div className="min-h-screen flex flex-col fade-in" data-testid="voimakortti-detail" key={card.core_key}>
        <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-6 sm:pb-8 grid grid-cols-3 items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="justify-self-start text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 bg-transparent border-0 p-0 cursor-pointer font-pressura"
            style={{ fontWeight: 700 }}
            data-testid="voimakortti-back-btn"
          >
            {t('nav.back')}
          </button>
          <span aria-hidden="true" className="justify-self-center" />
          <span
            className="justify-self-end text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] opacity-60 font-pressura"
            style={{ fontWeight: 700 }}
            data-testid="voimakortti-detail-label"
          >
            {t('nav.voimakortit')}
          </span>
        </header>
        <main className="flex-1 w-full px-4 sm:px-10 pb-24 flex flex-col items-center">
          {img && (
            <img
              src={img}
              alt={cleanTitle(card.title)}
              className="max-w-full w-full sm:w-[460px] h-auto object-contain mb-10 sm:mb-14"
              loading="eager"
              data-testid={`voimakortti-image-${card.core_key}`}
            />
          )}
          <h1
            className="font-pressura font-bold text-[#000000] text-center max-w-3xl uppercase tracking-[0.04em] leading-tight"
            style={{ fontSize: 'clamp(1.5rem, 4vw, 2.75rem)' }}
            data-testid={`voimakortti-title-${card.core_key}`}
          >
            {cleanTitle(card.title)}
          </h1>
          <article
            className="w-full max-w-2xl mt-12 sm:mt-16 px-2 sm:px-6 text-[#2A2724] font-pressura whitespace-pre-wrap text-sm sm:text-base leading-relaxed"
            data-testid={`voimakortti-body-${card.core_key}`}
          >
            {bodyOf(card.text)}
          </article>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col fade-in" data-testid="voimakortit-page">
      <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-6 sm:pb-8 grid grid-cols-3 items-center gap-2">
        <Link
          to="/"
          className="justify-self-start text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 font-pressura"
          style={{ fontWeight: 700 }}
          data-testid="voimakortit-home-link"
        >
          {t('nav.back')}
        </Link>
        <span aria-hidden="true" className="justify-self-center" />
        <span
          className="justify-self-end text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] opacity-60 font-pressura"
          style={{ fontWeight: 700 }}
          data-testid="voimakortit-page-label"
        >
          {t('nav.voimakortit')}
        </span>
      </header>
      <main className="flex-1 w-full px-4 sm:px-10 pb-20 flex flex-col items-center">
        <h1
          className="font-pressura font-bold text-[#000000] text-center mt-4 sm:mt-6 uppercase tracking-[0.06em]"
          style={{ fontSize: 'clamp(1.625rem, 4.5vw, 3rem)' }}
          data-testid="voimakortit-h1"
        >
          {t('nav.voimakortit')}
        </h1>
        <p
          className="font-pressura italic text-[#000000] text-center mt-4 sm:mt-5 max-w-xl leading-relaxed opacity-70 text-sm sm:text-base"
          data-testid="voimakortit-intro"
        >
          {t('voimakortit.intro')}
        </p>

        {loading && (
          <p className="text-sm opacity-60 mt-16 font-pressura" data-testid="voimakortit-loading">
            {t('gate.connecting')}
          </p>
        )}
        {err && !loading && (
          <p className="text-sm text-red-700 mt-16 font-pressura" data-testid="voimakortit-error">
            {err}
          </p>
        )}

        {!loading && !err && (
          <ul className="w-full max-w-2xl mt-12 sm:mt-16 divide-y divide-[#000000]/15" data-testid="voimakortit-list">
            {cards.map((card, i) => {
              const img = imgFor(card);
              return (
                <li key={card.core_key || i}>
                  <button
                    type="button"
                    onClick={() => setOpen(card)}
                    className="w-full text-left py-4 sm:py-5 hover:opacity-60 transition-opacity duration-300 bg-transparent border-0 cursor-pointer font-pressura flex items-center gap-4 sm:gap-6"
                    data-testid={`voimakortti-card-${card.core_key}`}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt=""
                        aria-hidden="true"
                        className="w-12 h-12 sm:w-16 sm:h-16 object-cover flex-shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <span className="w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0" aria-hidden="true" />
                    )}
                    <span
                      className="flex-1 text-[#000000] uppercase tracking-[0.06em] text-base sm:text-lg"
                      style={{ fontWeight: 700 }}
                    >
                      {String(i + 1).padStart(2, '0')} · {cleanTitle(card.title)}
                    </span>
                    <span className="text-[#000000] opacity-50 text-sm" aria-hidden="true">→</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
